// Formatting and parsing helpers (ARCHITECTURE §2: money is integer pence; dates display as `dd Mon yyyy`, en-GB, GBP).
// Pure functions — safe to use anywhere, unit tested in format.test.ts.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

// ---------------------------------------------------------------- money

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const gbpWhole = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const grouped = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export interface MoneyFormatOptions {
  /** Drop the pence when the amount is a whole number of pounds (e.g. "£325,000"). Default false. */
  wholePounds?: boolean;
  /** Prefix positive amounts with "+" (for signed movements). Default false. */
  signed?: boolean;
}

/**
 * Integer pence → "£1,234.56" (en-GB, GBP). Display only — never do arithmetic on the result.
 * `null`/`undefined` → "—".
 */
export function formatMoney(pence: number | null | undefined, opts: MoneyFormatOptions = {}): string {
  if (pence === null || pence === undefined || !Number.isFinite(pence)) return '—';
  const pounds = pence / 100;
  const useWhole = opts.wholePounds === true && pence % 100 === 0;
  const text = (useWhole ? gbpWhole : gbp).format(pounds);
  // Normalise the minus sign and avoid "-£0.00".
  const normalised = Object.is(pounds, -0) || pence === 0 ? text.replace('-', '') : text;
  return opts.signed && pence > 0 ? `+${normalised}` : normalised;
}

/** Integer pence → plain number text for an input, e.g. 123456 → "1,234.56" (or "1234.56" with grouping=false). */
export function penceToInput(pence: number | null | undefined, grouping = true): string {
  if (pence === null || pence === undefined || !Number.isFinite(pence)) return '';
  if (grouping) return grouped.format(pence / 100);
  const negative = pence < 0;
  const abs = Math.abs(Math.trunc(pence));
  const pounds = Math.floor(abs / 100);
  const p = String(abs % 100).padStart(2, '0');
  return `${negative ? '-' : ''}${pounds}.${p}`;
}

/**
 * Parse a pounds string typed by a person into integer pence, without floating point.
 * Accepts "1234", "1,234.5", "£1,234.56", " 12.30 ", "-5" (when allowNegative). Returns null if invalid
 * (letters, more than 2 decimal places, misplaced commas, empty).
 */
export function parsePoundsToPence(input: string, opts: { allowNegative?: boolean } = {}): number | null {
  let s = input.trim().replace(/\s+/g, '');
  if (!s) return null;
  let negative = false;
  if (s.startsWith('-') || s.startsWith('−')) {
    negative = true;
    s = s.slice(1);
  }
  if (s.startsWith('£')) s = s.slice(1);
  if (s.startsWith('-') && !negative) {
    negative = true;
    s = s.slice(1);
  }
  if (negative && !opts.allowNegative) return null;
  // Commas only as thousands separators.
  if (s.includes(',')) {
    if (!/^\d{1,3}(,\d{3})*(\.\d*)?$/.test(s)) return null;
    s = s.replace(/,/g, '');
  }
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m) return null;
  const whole = m[1] ?? '';
  const frac = m[2] ?? '';
  if (whole === '' && frac === '') return null;
  const pounds = whole === '' ? 0 : Number(whole);
  const pence = frac === '' ? 0 : Number(frac.padEnd(2, '0'));
  const total = pounds * 100 + pence;
  if (!Number.isSafeInteger(total)) return null;
  return negative ? -total : total;
}

// ---------------------------------------------------------------- dates

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** True if y-m-d is a real calendar date. */
export function isValidYmd(y: number, m: number, d: number): boolean {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (y < 1800 || y > 2200 || m < 1 || m > 12 || d < 1) return false;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return d <= days;
}

export function toIsoDate(y: number, m: number, d: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** Today's date in the user's local time as `yyyy-mm-dd`. */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/**
 * ISO date (`2026-10-12`) or timestamp → "12 Oct 2026". Calendar dates are formatted from their parts, so
 * there is no time-zone shift. Timestamps use the user's local date. Invalid/empty → "—".
 */
export function formatDate(iso: string | null | undefined, opts: { longMonth?: boolean } = {}): string {
  if (!iso) return '—';
  const months = opts.longMonth ? MONTHS_LONG : MONTHS;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  if (dateOnly) {
    const m = ISO_DATE.exec(iso);
    if (!m) return '—';
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!isValidYmd(y, mo, d)) return '—';
    return `${pad2(d)} ${months[mo - 1]} ${y}`;
  }
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return `${pad2(dt.getDate())} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
}

/** Timestamp → "12 Oct 2026, 14:05" (local time, 24-hour). */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return `${formatDate(iso)}, ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

/** Relative wording for recent timestamps: "just now", "5 minutes ago", "3 days ago", else the date. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  const secs = Math.round((now.getTime() - dt.getTime()) / 1000);
  if (secs < 0) return formatDate(iso);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(iso);
}

/**
 * Parse a date typed by a person into ISO `yyyy-mm-dd`. Accepts "12 Oct 2026", "12 October 2026",
 * "12/10/2026", "12-10-2026", "12.10.26", "2026-10-12". Two-digit years are 2000-based when <= 69.
 * Returns null when not a real date.
 */
export function parseDateInput(input: string): string | null {
  const s = input.trim().replace(/\s+/g, ' ').replace(/,/g, '');
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    return isValidYmd(y, mo, d) ? toIsoDate(y, mo, d) : null;
  }
  m = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const d = Number(m[1]);
    const mo = Number(m[2]);
    const y = normaliseYear(m[3] ?? '');
    return isValidYmd(y, mo, d) ? toIsoDate(y, mo, d) : null;
  }
  m = /^(\d{1,2})(?:st|nd|rd|th)? ([A-Za-z]+)\.? (\d{2}|\d{4})$/.exec(s);
  if (m) {
    const d = Number(m[1]);
    const mo = monthFromName(m[2] ?? '');
    const y = normaliseYear(m[3] ?? '');
    return mo && isValidYmd(y, mo, d) ? toIsoDate(y, mo, d) : null;
  }
  return null;
}

function normaliseYear(y: string): number {
  if (y.length === 2) {
    const n = Number(y);
    return n <= 69 ? 2000 + n : 1900 + n;
  }
  return Number(y);
}

function monthFromName(name: string): number | null {
  const n = name.toLowerCase();
  if (n.length < 3) return null;
  const idx = MONTHS_LONG.findIndex((full) => full.toLowerCase().startsWith(n.slice(0, 3)) && full.toLowerCase().startsWith(n));
  if (idx >= 0) return idx + 1;
  if (n === 'sept') return 9;
  return null;
}

// ---------------------------------------------------------------- UK tax years (6 April – 5 April)

/** Tax-year key for an ISO date, e.g. "2026-10-12" → "2026-27"; "2026-04-05" → "2025-26". */
export function taxYearFor(iso: string): string {
  const m = ISO_DATE.exec(iso);
  if (!m) throw new Error(`Invalid date: ${iso}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const start = mo > 4 || (mo === 4 && d >= 6) ? y : y - 1;
  return `${start}-${pad2((start + 1) % 100)}`;
}

/** Start and end ISO dates of a tax year key ("2026-27" → 2026-04-06 … 2027-04-05). */
export function taxYearRange(taxYear: string): { start: string; end: string } {
  const start = Number(taxYear.slice(0, 4));
  return { start: toIsoDate(start, 4, 6), end: toIsoDate(start + 1, 4, 5) };
}

/** "2026-27" → "2026/27"; with `long` → "2026/27 tax year (06 Apr 2026 to 05 Apr 2027)". */
export function taxYearLabel(taxYear: string, opts: { long?: boolean } = {}): string {
  const short = taxYear.replace('-', '/');
  if (!opts.long) return short;
  const { start, end } = taxYearRange(taxYear);
  return `${short} tax year (${formatDate(start)} to ${formatDate(end)})`;
}

// ---------------------------------------------------------------- misc

/** "1 item" / "3 items". */
export function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/** Bytes → "2.4 MB". */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
